/**
 * Board — row-per-account whiteboard of next-actions.
 *
 * UI sprint step 7 (2026-05-11): ported from Wayne's Brain pwa-v2's Board.
 * The KA build differs from PWAv2 in that next-actions live in the
 * canonical `next_actions` table (data-model C.1), not as JSON in
 * account metadata. Each chip → /api/next-actions row; drag → bulk
 * reorder via /api/next-actions/reorder; color cycle → PATCH.
 *
 * Filters: Clients / Prospects sections by metadata.health (matches
 * PWAv2). Empty health = "Clients" bucket.
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import Sortable from 'sortablejs';
import {
  completeNextAction,
  createNextAction,
  dismissNextAction,
  type EntityListItem,
  listEntities,
  listNextActions,
  type NextAction,
  patchNextAction,
  reorderNextActions,
} from '../api.ts';
import { Avatar } from '../components/primitives';
import { Icons } from '../components/icons';

type ItemColor = 'none' | 'green' | 'yellow' | 'red';
const COLOR_CYCLE: ItemColor[] = ['none', 'green', 'yellow', 'red'];

function nextColor(current: 'green' | 'yellow' | 'red' | null): ItemColor {
  const cur: ItemColor = current ?? 'none';
  return COLOR_CYCLE[(COLOR_CYCLE.indexOf(cur) + 1) % COLOR_CYCLE.length]!;
}

/**
 * Format a due-date ISO string for chip subtext display. Same-year
 * dates omit the year ("May 15"); other years include it ("May 15,
 * 2027"). Falls back to the raw string on parse failure.
 */
function formatDueDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function pillClassForType(t: string): string {
  const v = (t || 'account').toLowerCase();
  if (v === 'account') return 'pill-info';
  return 'pill-muted';
}

interface BoardRowProps {
  account: EntityListItem;
  items: NextAction[];
  onChanged: () => void;
}

function BoardRow({ account, items, onChanged }: BoardRowProps) {
  const [local, setLocal] = useState<NextAction[]>(items);
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editSub, setEditSub] = useState('');
  const [busy, setBusy] = useState(false);
  const addRef = useRef<HTMLInputElement>(null);
  const editTextRef = useRef<HTMLInputElement>(null);
  const editSubRef = useRef<HTMLInputElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const sortableRef = useRef<Sortable | null>(null);
  const localRef = useRef<NextAction[]>(items);
  // Re-entrancy guards: Enter on the add/edit input fires the handler,
  // setAdding(false) tears down the DOM node which fires onBlur, which
  // would otherwise call the handler a second time before state has
  // flushed — producing a duplicate row in the DB.
  const submittingAddRef = useRef(false);
  const submittingEditRef = useRef(false);

  useEffect(() => {
    setLocal(items);
    localRef.current = items;
  }, [items]);

  useEffect(() => {
    localRef.current = local;
  }, [local]);

  useEffect(() => {
    if (adding) addRef.current?.focus();
  }, [adding]);

  useEffect(() => {
    if (editId) editTextRef.current?.focus();
  }, [editId]);

  useEffect(() => {
    if (!itemsRef.current) return;
    sortableRef.current?.destroy();
    sortableRef.current = Sortable.create(itemsRef.current, {
      animation: 150,
      delay: 150,
      delayOnTouchOnly: true,
      draggable: '.board-item-draggable',
      ghostClass: 'board-item-ghost',
      chosenClass: 'board-item-chosen',
      filter: '.board-add-btn, .board-item-adding',
      preventOnFilter: false,
      onEnd: async (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return;
        const current = [...localRef.current];
        const [moved] = current.splice(oldIndex, 1);
        if (!moved) return;
        current.splice(newIndex, 0, moved);
        const repositioned = current.map((it, i) => ({ ...it, position: i }));
        setLocal(repositioned);
        localRef.current = repositioned;
        setBusy(true);
        try {
          await reorderNextActions(
            repositioned.map((it) => ({ id: it.id, position: it.position })),
          );
        } catch (err) {
          console.error('reorder failed', err);
        } finally {
          setBusy(false);
          onChanged();
        }
      },
    });
    return () => {
      sortableRef.current?.destroy();
    };
  }, [account.id]);

  async function addItem() {
    if (submittingAddRef.current) return; // re-entry guard (Enter + blur race)
    const trimmed = newText.trim();
    if (!trimmed) {
      setAdding(false);
      return;
    }
    submittingAddRef.current = true;
    setBusy(true);
    try {
      const created = await createNextAction({ entity_id: account.id, text: trimmed });
      setLocal((prev) => [...prev, created]);
      localRef.current = [...localRef.current, created];
      setNewText('');
      setAdding(false);
      onChanged();
    } catch (err) {
      console.error('create failed', err);
    } finally {
      setBusy(false);
      submittingAddRef.current = false;
    }
  }

  async function deleteItem(id: string) {
    setBusy(true);
    try {
      await dismissNextAction(id);
      setLocal((prev) => prev.filter((i) => i.id !== id));
      localRef.current = localRef.current.filter((i) => i.id !== id);
      onChanged();
    } catch (err) {
      console.error('dismiss failed', err);
    } finally {
      setBusy(false);
    }
  }

  async function cycleItemColor(id: string) {
    const item = local.find((i) => i.id === id);
    if (!item) return;
    const next = nextColor(item.color);
    const sendColor = next === 'none' ? null : next;
    setBusy(true);
    setLocal((prev) =>
      prev.map((i) => (i.id === id ? { ...i, color: sendColor } : i)),
    );
    try {
      await patchNextAction(id, { color: sendColor });
      onChanged();
    } catch (err) {
      console.error('color failed', err);
    } finally {
      setBusy(false);
    }
  }

  function startEdit(item: NextAction) {
    setEditId(item.id);
    setEditText(item.text);
    setEditSub(item.sub ?? '');
  }

  async function commitEdit() {
    if (!editId) return;
    if (submittingEditRef.current) return; // re-entry guard
    const trimmed = editText.trim();
    if (!trimmed) {
      submittingEditRef.current = true;
      try {
        await deleteItem(editId);
      } finally {
        setEditId(null);
        setEditText('');
        setEditSub('');
        submittingEditRef.current = false;
      }
      return;
    }
    const trimmedSub = editSub.trim();
    // Find the existing row so we can avoid a no-op PATCH.
    const existing = local.find((i) => i.id === editId);
    const sameText = existing?.text === trimmed;
    const sameSub = (existing?.sub ?? '') === trimmedSub;
    if (sameText && sameSub) {
      setEditId(null);
      setEditText('');
      setEditSub('');
      return;
    }

    submittingEditRef.current = true;
    setBusy(true);
    try {
      const updated = await patchNextAction(editId, {
        text: trimmed,
        sub: trimmedSub.length > 0 ? trimmedSub : null,
      });
      setLocal((prev) => prev.map((i) => (i.id === editId ? updated : i)));
      onChanged();
    } catch (err) {
      console.error('edit failed', err);
    } finally {
      setBusy(false);
      setEditId(null);
      setEditText('');
      setEditSub('');
      submittingEditRef.current = false;
    }
  }

  const tierMeta = account.metadata.tier ? String(account.metadata.tier) : null;

  return (
    <div class="board-row">
      <div class="board-account">
        <Avatar name={account.name} size={26} />
        <div class="board-account-meta">
          <a
            class="board-account-name"
            href={`/entity/${account.id}`}
            title={account.name}
            style={{ display: 'block', textDecoration: 'none' }}
          >
            {account.name}
          </a>
          <div class="board-account-chips">
            <span class={`v2-pill ${pillClassForType(account.type)}`}>
              <span class="dot" />
              {account.type}
            </span>
            {tierMeta && (
              <span class="v2-chip">
                <span style={{ color: 'var(--text-muted)', fontSize: 10 }}>tier</span>
                <span class="mono">{tierMeta}</span>
              </span>
            )}
          </div>
        </div>
      </div>
      <div class="board-items" ref={itemsRef}>
        {local.map((item) => (
          <div
            key={item.id}
            data-id={item.id}
            class={`board-item board-item-draggable ${item.color ? `board-item-${item.color}` : ''}`}
          >
            <button
              type="button"
              class={`board-color-dot dot-${item.color ?? 'none'}`}
              onClick={(e) => {
                e.stopPropagation();
                cycleItemColor(item.id);
              }}
              disabled={busy}
              title="Cycle color"
              aria-label="Cycle color"
            />
            {editId === item.id ? (
              <div class="board-item-edit">
                <input
                  ref={editTextRef}
                  class="board-item-input"
                  value={editText}
                  onInput={(e) => setEditText((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit();
                    }
                    if (e.key === 'Escape') {
                      setEditId(null);
                      setEditText('');
                      setEditSub('');
                    }
                    if (e.key === 'Tab' && !e.shiftKey) {
                      e.preventDefault();
                      editSubRef.current?.focus();
                    }
                  }}
                  onBlur={(e) => {
                    // Allow focus to move to the sub input without committing.
                    const related = e.relatedTarget as HTMLElement | null;
                    if (related === editSubRef.current) return;
                    commitEdit();
                  }}
                  disabled={busy}
                />
                <input
                  ref={editSubRef}
                  class="board-item-input board-item-sub-input"
                  placeholder="detail…"
                  value={editSub}
                  onInput={(e) => setEditSub((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit();
                    }
                    if (e.key === 'Escape') {
                      setEditId(null);
                      setEditText('');
                      setEditSub('');
                    }
                    if (e.key === 'Tab' && e.shiftKey) {
                      e.preventDefault();
                      editTextRef.current?.focus();
                    }
                  }}
                  onBlur={(e) => {
                    // Don't commit if focus moved between the two inputs
                    // — only commit when focus leaves both.
                    const related = e.relatedTarget as HTMLElement | null;
                    if (related === editTextRef.current) return;
                    commitEdit();
                  }}
                  disabled={busy}
                />
              </div>
            ) : (
              <>
                <div
                  class="board-item-content"
                  onClick={() => startEdit(item)}
                  style={{ cursor: 'text' }}
                >
                  <span class="board-item-text">{item.text}</span>
                  {(() => {
                    // Sub line precedence: explicit user-set `sub` first,
                    // else derive from `due_date` so AI-extracted next-
                    // actions with a date surface "due {date}" inline.
                    const displaySub =
                      item.sub && item.sub.trim().length > 0
                        ? item.sub
                        : item.due_date
                          ? `due ${formatDueDate(item.due_date)}`
                          : null;
                    return displaySub ? (
                      <span class="board-item-sub">{displaySub}</span>
                    ) : null;
                  })()}
                </div>
                <button
                  type="button"
                  class="board-item-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteItem(item.id);
                  }}
                  disabled={busy}
                  title="Dismiss"
                  aria-label="Dismiss"
                >
                  <Icons.Close size={12} />
                </button>
              </>
            )}
          </div>
        ))}
        {adding ? (
          <div class="board-item board-item-adding">
            <span class="board-color-dot dot-none" />
            <input
              ref={addRef}
              class="board-item-input"
              placeholder="Work item…"
              value={newText}
              onInput={(e) => setNewText((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addItem();
                if (e.key === 'Escape') {
                  setAdding(false);
                  setNewText('');
                }
              }}
              onBlur={() => {
                if (!newText.trim()) setAdding(false);
                else addItem();
              }}
              disabled={busy}
            />
          </div>
        ) : (
          <button
            type="button"
            class="board-add-btn"
            onClick={() => setAdding(true)}
            title="Add item"
            aria-label="Add item"
          >
            <Icons.Plus size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Page
// ───────────────────────────────────────────────────────────────────────────

export function Board() {
  const [accounts, setAccounts] = useState<EntityListItem[] | null>(null);
  const [items, setItems] = useState<NextAction[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load() {
    Promise.all([listEntities({ type: 'account' }), listNextActions()])
      .then(([a, n]) => {
        setAccounts(a);
        setItems(n);
      })
      .catch((err: Error) => setError(err.message));
  }

  useEffect(() => {
    load();
  }, []);

  const itemsByAccount = useMemo(() => {
    const map = new Map<string, NextAction[]>();
    for (const n of items ?? []) {
      const list = map.get(n.entity_id) ?? [];
      list.push(n);
      map.set(n.entity_id, list);
    }
    // sort each by position
    for (const [, list] of map) {
      list.sort((a, b) => a.position - b.position);
    }
    return map;
  }, [items]);

  const visibleAccounts = useMemo(() => {
    if (!accounts) return [];
    // Match PWAv2: exclude partner / competitor / employer by health.
    // Everything else stays — splits into Active vs Prospects below.
    const excluded = new Set(['competitor', 'partner', 'employer']);
    return accounts.filter((a) => {
      const h = String(a.metadata.health ?? '').toLowerCase();
      return !excluded.has(h);
    });
  }, [accounts]);

  const active = useMemo(
    () =>
      visibleAccounts
        .filter((a) => String(a.metadata.health ?? '').toLowerCase() === 'active')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [visibleAccounts],
  );

  const prospects = useMemo(
    () =>
      visibleAccounts
        .filter((a) => String(a.metadata.health ?? '').toLowerCase() !== 'active')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [visibleAccounts],
  );

  const totalItems = items?.length ?? 0;
  const redCount = (items ?? []).filter((i) => i.color === 'red').length;

  if (error) {
    return (
      <div class="v2-page wide board">
        <div class="page-head">
          <div>
            <div class="page-eyebrow">Whiteboard</div>
            <h1 class="v2-page-title">Board</h1>
          </div>
        </div>
        <div class="error-banner">{error}</div>
      </div>
    );
  }

  if (!accounts || !items) {
    return (
      <div class="v2-page wide board">
        <div class="page-head">
          <div>
            <div class="page-eyebrow">Whiteboard · loading</div>
            <h1 class="v2-page-title">Board</h1>
          </div>
        </div>
        <div class="skel" style={{ height: 200, borderRadius: 'var(--r-lg)' }} />
      </div>
    );
  }

  return (
    <div class="v2-page wide board">
      <div class="page-head">
        <div>
          <div class="page-eyebrow">Whiteboard · {visibleAccounts.length} accounts</div>
          <h1 class="v2-page-title">Board</h1>
          <div class="page-sub">
            {totalItems} {totalItems === 1 ? 'item' : 'items'} across{' '}
            {visibleAccounts.length} {visibleAccounts.length === 1 ? 'account' : 'accounts'}
            {redCount > 0 && (
              <>
                {' '}
                · <span style={{ color: 'var(--danger)' }}>{redCount} urgent</span>
              </>
            )}
          </div>
        </div>
        <div class="board-legend">
          <span class="board-legend-item">
            <span class="board-color-dot dot-red" /> Blocked
          </span>
          <span class="board-legend-item">
            <span class="board-color-dot dot-yellow" /> Attention
          </span>
          <span class="board-legend-item">
            <span class="board-color-dot dot-green" /> On track
          </span>
          <span class="board-legend-item">
            <span class="board-color-dot dot-none" /> Unset
          </span>
        </div>
      </div>

      {active.length > 0 && (
        <>
          <div class="board-section">
            <span class="board-section-label">Active</span>
            <span class="board-section-count">{active.length}</span>
          </div>
          <div class="board-table">
            {active.map((a) => (
              <BoardRow
                key={a.id}
                account={a}
                items={itemsByAccount.get(a.id) ?? []}
                onChanged={load}
              />
            ))}
          </div>
        </>
      )}

      {prospects.length > 0 && (
        <>
          <div class="board-section">
            <span class="board-section-label">Prospects</span>
            <span class="board-section-count">{prospects.length}</span>
          </div>
          <div class="board-table">
            {prospects.map((a) => (
              <BoardRow
                key={a.id}
                account={a}
                items={itemsByAccount.get(a.id) ?? []}
                onChanged={load}
              />
            ))}
          </div>
        </>
      )}

      {active.length === 0 && prospects.length === 0 && (
        <div
          class="panel"
          style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}
        >
          No accounts yet. Add some from the Portfolio page to populate the Board.
        </div>
      )}
    </div>
  );
}
